import { useState } from "react";
import { ScreenMax } from "styles/antTheme/Variables";
import { useMediaQuery } from "usehooks-ts";

import { cn } from "@btnet/utilities-core";

import { BdsIconCheckCircle, BdsIconCornersOut, BdsText } from "@buildertrend/components";

import { BuilderInfoContext } from "helpers/globalContext/BuilderInfoContext";
import { useRequiredContext } from "helpers/globalContext/useRequiredContext";

import { BTAlert } from "commonComponents/btWrappers/BTAlert/BTAlert";
import { BTButton } from "commonComponents/btWrappers/BTButton/BTButton";

import {
    type BillViewModePreference,
    getBillViewModePreference,
    setBillViewModePreference,
} from "entity/bill/common/resolveBillViewMode";

export interface IBillFullPageBannerProps {
    onViewModeChange: (mode: BillViewModePreference) => void;
    className?: string;
}

export const BillFullPageBanner: React.FC<IBillFullPageBannerProps> = ({
    onViewModeChange,
    className,
}) => {
    const builderInfo = useRequiredContext(BuilderInfoContext);
    const isXsScreen = useMediaQuery(`(max-width: ${ScreenMax.xs}px)`);
    const [viewMode, setViewMode] = useState<BillViewModePreference>(getBillViewModePreference);

    if (!builderInfo?.flags?.billsFullPageConversion) {
        return null;
    }

    const switchViewMode = (next: BillViewModePreference) => {
        setBillViewModePreference(next);
        setViewMode(next);
        onViewModeChange(next);
    };

    if (viewMode === "fullPage") {
        const switchBackButton = (
            <BTButton
                data-testid="switchToPopUpBillButton"
                type="secondary"
                className={isXsScreen ? "margin-top-xs" : ""}
                onClick={() => switchViewMode("modal")}
            >
                Switch back
            </BTButton>
        );

        return (
            <BTAlert
                message={
                    <BdsText size="normal-md">
                        You're making the most of the space with full-page bills.
                    </BdsText>
                }
                type="success"
                data-testid="billFullPageActiveBanner"
                showIcon
                icon={<BdsIconCheckCircle size="md" />}
                className={cn(className)}
                action={isXsScreen ? undefined : switchBackButton}
                style={{ flex: 1 }}
            />
        );
    }

    const tryButton = (
        <BTButton
            data-testid="tryFullPageBillButton"
            className={isXsScreen ? "margin-top-xs" : ""}
            onClick={() => switchViewMode("fullPage")}
        >
            Try it
        </BTButton>
    );

    return (
        <BTAlert
            message={
                <>
                    <BdsText size="normal-md">
                        Give every bill room to breathe with the new full-page view. You can switch
                        back anytime.
                    </BdsText>
                    {isXsScreen && tryButton}
                </>
            }
            type="info"
            data-testid="billFullPageOptInBanner"
            showIcon
            icon={<BdsIconCornersOut size="md" />}
            className={cn(className)}
            action={isXsScreen ? undefined : tryButton}
            style={{ flex: 1 }}
        />
    );
};
